import { Edit } from "@refinedev/antd";
import { useNavigation, useOne, useParsed } from "@refinedev/core";
import { useABContext } from "@src/contexts/ab-testing/ab-context";
import {
  parsedChartFormValueFromApi,
  parsedFilterFormValueFromApi,
} from "@src/util/ab-testing/handle-init-value-for-ab-test";
import { FilterFormFieldsEnum } from "./enum";
import { getUserInfo } from "@src/util/get-user-info";
import React from "react";
import { PAGE_URL } from "@src/config";
import { useUpdateCacheInitAbConfig } from "@src/hooks/ab-testing/use-update-cache-init-ab-config";
import { shared } from "@src/util/shared";
import { CUSTOM_EVENT_NAME } from "@src/constant/ab-testing/custom-event-name.enum";
import { Error403 } from "../error/403";
import { useMe } from "@src/hooks/use-me";
import { ABTestContainer } from "./component/ad-test-container";

export const INIT_CONFIG_SHARED_KEY = "init_config_shared_key";
export const DEFAULT_TITLE = "AB test exploration";
export const ABTestExploreEdit: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { push } = useNavigation();
  const { isSuperAdmin } = useMe();

  const [dontHavePermission, setDontHavePermission] = React.useState(false);
  const userInfo = getUserInfo();
  const { filterForm, chartForm } = useABContext();
  const { cacheAbConfig } = useUpdateCacheInitAbConfig();
  const params = useParsed();
  const id = params.id;

  const handleSetInitialValue = (data: any) => {
    const config = data.config;
    const parsedGeneralConfig = parsedFilterFormValueFromApi(config?.generalConfig);

    const parsedChartConfig = parsedChartFormValueFromApi(config?.chartConfig);

    filterForm.setFieldsValue({
      ...parsedGeneralConfig,
      [FilterFormFieldsEnum.IS_FIRST_APPLIED]: false,
      [FilterFormFieldsEnum.TITLE]: data.title ?? DEFAULT_TITLE,
      [FilterFormFieldsEnum.DESCRIPTION]: data.description,
    });
    chartForm.setFieldsValue(parsedChartConfig);
    cacheAbConfig(data);
  };
  const { isLoading, isFetching } = useOne({
    dataProviderName: "nestjsx",
    resource: "explorations",
    id,
    queryOptions: {
      onSuccess: (data) => {
        if (!data.data) return;
        if (!isSuperAdmin && data?.data?.owner !== userInfo?.email && !data?.data?.is_public) {
          setDontHavePermission(true);
        }

        handleSetInitialValue(data?.data);
      },
      onError: (err) => {
        if (err?.response?.status === 403) {
          setDontHavePermission(true);
        }
      },
      onSettled: () => {
        window.dispatchEvent(
          new CustomEvent(CUSTOM_EVENT_NAME.GET_AB_TEST_LOADING, {
            detail: {
              loading: false,
            },
          }),
        );
      },

      queryKey: ["explorations", "edit", id],
    },
  });

  React.useEffect(() => {
    if (!filterForm || !chartForm) return;

    return () => {
      shared.delete(INIT_CONFIG_SHARED_KEY);
    };
  }, []);

  if (dontHavePermission) {
    return (
      <Error403
        message="You don't have permission to view this page. Please contact our admin for support
"
        onBack={() => {
          push(PAGE_URL.EXPLORATION.ROOT);
        }}
      />
    );
  }
  const loading = isLoading || isFetching;

  return (
    <ABTestContainer>
      <Edit
        isLoading={loading}
        title={<div />}
        headerButtons={<div className="hidden" />}
        contentProps={{
          className: "bg-transparent shadow-none card-custom",
        }}
        breadcrumb={null}
        footerButtonProps={{
          hidden: true,
        }}
        wrapperProps={{
          className: "base-custom-container",
        }}
      >
        {children}
      </Edit>
    </ABTestContainer>
  );
};
